import { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import { Globe, Mail, Lock, User, Calendar, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Auth = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({ email: '', password: '', name: '', date_of_birth: '', referral_code: '' });
  const [dobMonth, setDobMonth] = useState('');
  const [dobDay, setDobDay] = useState('');
  const [dobYear, setDobYear] = useState('');
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [keepSignedIn, setKeepSignedIn] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  // Check for referral code in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const ref = urlParams.get('ref');
    if (ref) {
      setFormData(prev => ({ ...prev, referral_code: ref }));
      setIsLogin(false); // Switch to register tab
    }
  }, []);

  const calculateAge = (dateOfBirth) => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Age validation for registration
    if (!isLogin) {
      // Validate DOB fields
      if (!dobMonth || !dobDay || !dobYear) {
        toast.error('Please enter your complete date of birth');
        setLoading(false);
        return;
      }

      // Validate month (1-12)
      const monthNum = parseInt(dobMonth);
      if (monthNum < 1 || monthNum > 12) {
        toast.error('Please enter a valid month (1-12)');
        setLoading(false);
        return;
      }

      // Validate day (1-31)
      const dayNum = parseInt(dobDay);
      if (dayNum < 1 || dayNum > 31) {
        toast.error('Please enter a valid day (1-31)');
        setLoading(false);
        return;
      }

      // Validate year
      const yearNum = parseInt(dobYear);
      const currentYear = new Date().getFullYear();
      if (yearNum < 1900 || yearNum > currentYear) {
        toast.error('Please enter a valid year');
        setLoading(false);
        return;
      }

      // Validate the date is valid (e.g., not Feb 31)
      // Create date using local timezone to avoid timezone shift issues
      const testDate = new Date(yearNum, monthNum - 1, dayNum);
      
      // Check if the date rolled over (e.g., Feb 31 becomes Mar 3)
      if (testDate.getMonth() !== monthNum - 1 || testDate.getDate() !== dayNum || testDate.getFullYear() !== yearNum) {
        toast.error('Please enter a valid date (e.g., February only has 28/29 days)');
        setLoading(false);
        return;
      }

      // Construct date in YYYY-MM-DD format
      const formattedMonth = dobMonth.padStart(2, '0');
      const formattedDay = dobDay.padStart(2, '0');
      const dateOfBirth = `${dobYear}-${formattedMonth}-${formattedDay}`;

      // Update formData with constructed date
      formData.date_of_birth = dateOfBirth;
      
      const age = calculateAge(dateOfBirth);
      if (age < 18) {
        toast.error('You must be 18 years or older to register');
        setLoading(false);
        return;
      }

      if (!ageConfirmed) {
        toast.error('Please confirm that you are 18 years or older');
        setLoading(false);
        return;
      }
    }

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const response = await axios.post(`${API}${endpoint}`, formData);
      
      // Store keepSignedIn preference
      if (isLogin) {
        localStorage.setItem('keepSignedIn', keepSignedIn ? 'true' : 'false');
      }
      
      login(response.data.token, response.data.user);
      toast.success(isLogin ? 'Welcome back!' : 'Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-coral-50 via-peach-50 to-teal-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Globe className="w-10 h-10 text-coral" />
            <h1 className="text-3xl font-bold">CultraMeet</h1>
          </div>
          <p className="text-gray-600">Connect with amazing people worldwide</p>
        </div>

        <div className="glass-dark rounded-3xl p-8 shadow-2xl">
          <div className="flex space-x-2 mb-6">
            <button
              data-testid="login-tab"
              onClick={() => setIsLogin(true)}
              className={`flex-1 py-3 rounded-full font-semibold transition-all ${
                isLogin ? 'bg-coral text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Login
            </button>
            <button
              data-testid="register-tab"
              onClick={() => setIsLogin(false)}
              className={`flex-1 py-3 rounded-full font-semibold transition-all ${
                !isLogin ? 'bg-teal text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Register
            </button>
          </div>

          {formData.referral_code && !isLogin && (
            <div className="mb-4 p-3 bg-teal-50 border border-teal-200 rounded-xl">
              <p className="text-sm text-teal-800">
                ✨ You were invited! Referral code: <span className="font-bold">{formData.referral_code}</span>
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <>
                <div className="mb-4 p-4 bg-red-50 border-2 border-red-200 rounded-xl">
                  <p className="text-sm font-semibold text-red-800 flex items-center">
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    Age Requirement: 18 Years or Older
                  </p>
                  <p className="text-xs text-red-700 mt-1">
                    You must be at least 18 years old to create an account and use CultraMeet.
                  </p>
                </div>
                <div>
                  <Label htmlFor="name" className="text-gray-700 font-medium">
                    Full Name
                  </Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="name"
                      data-testid="name-input"
                      type="text"
                      placeholder="Enter your name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="pl-10 py-6 rounded-xl border-gray-200"
                      required={!isLogin}
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-gray-700 font-medium flex items-center">
                    <Calendar className="w-4 h-4 mr-2" />
                    Date of Birth
                  </Label>
                  <div className="relative mt-1">
                    <div className="flex items-center bg-white border-2 border-gray-200 rounded-xl p-2 focus-within:border-teal-500 focus-within:ring-2 focus-within:ring-teal-200">
                      {/* Month */}
                      <div className="flex-1 flex flex-col items-center px-2">
                        <input
                          id="dob-month"
                          data-testid="dob-month"
                          type="text"
                          inputMode="numeric"
                          placeholder="MM"
                          value={dobMonth}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, '');
                            if (val === '' || (parseInt(val) >= 1 && parseInt(val) <= 12 && val.length <= 2)) {
                              setDobMonth(val);
                              // Auto-advance to day field when month is complete
                              if (val.length === 2) {
                                document.getElementById('dob-day').focus();
                              }
                            }
                          }}
                          maxLength="2"
                          className="w-full text-center text-lg font-medium border-0 outline-none bg-transparent"
                          required={!isLogin}
                        />
                        <span className="text-xs text-gray-400 mt-1">Month</span>
                      </div>
                      
                      <span className="text-2xl text-gray-300 font-light">/</span>
                      
                      {/* Day */}
                      <div className="flex-1 flex flex-col items-center px-2">
                        <input
                          id="dob-day"
                          data-testid="dob-day"
                          type="text"
                          inputMode="numeric"
                          placeholder="DD"
                          value={dobDay}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, '');
                            if (val === '' || (parseInt(val) >= 1 && parseInt(val) <= 31 && val.length <= 2)) {
                              setDobDay(val);
                              // Auto-advance to year field when day is complete
                              if (val.length === 2) {
                                document.getElementById('dob-year').focus();
                              }
                            }
                          }}
                          maxLength="2"
                          className="w-full text-center text-lg font-medium border-0 outline-none bg-transparent"
                          required={!isLogin}
                        />
                        <span className="text-xs text-gray-400 mt-1">Day</span>
                      </div>
                      
                      <span className="text-2xl text-gray-300 font-light">/</span>
                      
                      {/* Year */}
                      <div className="flex-1 flex flex-col items-center px-2">
                        <input
                          id="dob-year"
                          data-testid="dob-year"
                          type="text"
                          inputMode="numeric"
                          placeholder="YYYY"
                          value={dobYear}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, '');
                            if (val === '' || (val.length <= 4 && !isNaN(val))) {
                              setDobYear(val);
                            }
                          }}
                          maxLength="4"
                          className="w-full text-center text-lg font-medium border-0 outline-none bg-transparent"
                          required={!isLogin}
                        />
                        <span className="text-xs text-gray-400 mt-1">Year</span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2 text-center">
                      💡 Example: 03 / 15 / 1995
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 p-4 bg-teal-50 rounded-xl border border-teal-200">
                  <Checkbox
                    id="age-confirm"
                    data-testid="age-confirm-checkbox"
                    checked={ageConfirmed}
                    onCheckedChange={setAgeConfirmed}
                    className="mt-1"
                  />
                  <Label htmlFor="age-confirm" className="text-sm text-gray-700 cursor-pointer leading-relaxed">
                    I confirm that I am 18 years of age or older and agree to the Terms of Service
                  </Label>
                </div>
              </>
            )}

            <div>
              <Label htmlFor="email" className="text-gray-700 font-medium">
                Email
              </Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="email"
                  data-testid="email-input"
                  type="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="pl-10 py-6 rounded-xl border-gray-200"
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-gray-700 font-medium">
                Password
              </Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="password"
                  data-testid="password-input"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="pl-10 pr-10 py-6 rounded-xl border-gray-200"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {isLogin && (
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="keep-signed-in"
                    checked={keepSignedIn}
                    onCheckedChange={setKeepSignedIn}
                  />
                  <Label htmlFor="keep-signed-in" className="text-sm text-gray-700 cursor-pointer">
                    Keep me signed in
                  </Label>
                </div>
                <div className="text-right">
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password')}
                    className="text-sm text-coral hover:underline"
                  >
                    Forgot password?
                  </button>
                </div>
              </div>
            )}

            <Button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full py-6 rounded-xl text-lg font-semibold bg-white text-black border-2 border-teal hover:bg-teal hover:text-white"
            >
              {loading ? 'Processing...' : isLogin ? 'Login' : 'Create Account'}
            </Button>
          </form>

          <p className="text-center text-gray-600 mt-6">
            {isLogin ? "Don't have an account?" : 'Already have an account?'}{' '}
            <button
              data-testid="toggle-auth-mode"
              onClick={() => setIsLogin(!isLogin)}
              className="text-coral font-semibold hover:underline"
            >
              {isLogin ? 'Register' : 'Login'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Auth;